import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Select } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'

import type { NotificationChannel } from '../types'

interface NotificationPreferenceRowProps {
  title: string
  description: string
  value: NotificationChannel
  onChange: (value: NotificationChannel) => void
  channelOptions: Array<{ value: NotificationChannel; labelKey: string }>
  showSeparator?: boolean
}

export const NotificationPreferenceRow: React.FC<
  NotificationPreferenceRowProps
> = ({
  title,
  description,
  value,
  onChange,
  channelOptions,
  showSeparator = false,
}) => {
  const { t } = useTranslation()

  const selectOptions = useMemo(
    () =>
      channelOptions.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    [channelOptions, t]
  )

  return (
    <div className="flex flex-col rounded-2xl">
      <div className="flex flex-col items-start gap-3 p-6 lg:flex-row lg:gap-14">
        <div className="w-full md:max-w-[560px]">
          <h4 className="m-0 text-sm font-semibold text-gray-light-700">
            {title}
          </h4>
          <p className="m-0 mt-1 text-sm text-tertiary-600">{description}</p>
        </div>

        <div className="flex-shrink-0 w-full md:w-50">
          <Select
            value={value}
            onChange={(newValue) => onChange(newValue as NotificationChannel)}
            options={selectOptions}
            fullWidth
            disableSorting
          />
        </div>
      </div>

      {showSeparator && (
        <Separator className="bg-gray-light-200 w-[96%] mx-auto" />
      )}
    </div>
  )
}
